import { useState, useEffect, useRef, useMemo } from "react";

// Load Poppins
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Poppins:wght@200;300;400;500;600&display=swap";
document.head.appendChild(fontLink);

// ─────────────────────────────────────────────────────────────────────────────
// HEX GRID — FLAT-TOP AXIAL COORDINATES
// ─────────────────────────────────────────────────────────────────────────────
const HEX_SIZE = 44;

function hexToPixel(q, r, size = HEX_SIZE) {
  return {
    x: size * (3 / 2) * q,
    y: size * ((Math.sqrt(3) / 2) * q + Math.sqrt(3) * r),
  };
}

function hexCorners(cx, cy, size = HEX_SIZE) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i);
    return [
      (cx + size * Math.cos(a)).toFixed(2),
      (cy + size * Math.sin(a)).toFixed(2),
    ].join(",");
  }).join(" ");
}

const HEX_DIRS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, -1],
  [-1, 1],
];
function hexKey(q, r) {
  return `${q},${r}`;
}
function parseKey(k) {
  const [q, r] = k.split(",").map(Number);
  return { q, r };
}
function hexDist(q, r) {
  return (Math.abs(q) + Math.abs(q + r) + Math.abs(r)) / 2;
}

function getRing(n) {
  if (n === 0) return [[0, 0]];
  const cells = [];
  let q = n,
    r = -n;
  const dirs = [
    [0, 1],
    [-1, 1],
    [-1, 0],
    [0, -1],
    [1, -1],
    [1, 0],
  ];
  for (let side = 0; side < 6; side++)
    for (let step = 0; step < n; step++) {
      cells.push([q, r]);
      q += dirs[side][0];
      r += dirs[side][1];
    }
  return cells;
}

function getNeighbors(q, r) {
  return HEX_DIRS.map(([dq, dr]) => [q + dq, r + dr]);
}

// All empty cells adjacent to any filled cell
function getFrontierCells(cells) {
  const frontier = new Set();
  Object.keys(cells).forEach((k) => {
    const { q, r } = parseKey(k);
    getNeighbors(q, r).forEach(([nq, nr]) => {
      const nk = hexKey(nq, nr);
      if (!cells[nk]) frontier.add(nk);
    });
  });
  return [...frontier];
}

// ─────────────────────────────────────────────────────────────────────────────
// THEME
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  dark: "#2e2a28",
  dark2: "#3d3530",
  gold: "#7d5846",
  honey: "#b9a498",
  amber: "#4b4545",
  cream: "#fbf8f1",
  text: "#e2ddd2",
  muted: "#c1bbbb",
  dimText: "#b9a49877",
  surface: "#919181",
  blush: "#d1b8ad",
  slate: "#a3a7ab",
};

const RING_FILLS = [
  ["#c9b8a8", "#7d5846"],
  ["#a89080", "#6b4535"],
  ["#919181", "#5a5a50"],
  ["#a3a7ab", "#6a7074"],
  ["#c1bbbb", "#8a8484"],
];
function ringFill(ring) {
  return RING_FILLS[Math.min(ring, RING_FILLS.length - 1)];
}

// ─────────────────────────────────────────────────────────────────────────────
// STEPS
// ─────────────────────────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 1,
    emoji: "1",
    label: "Listen",
    subtitle: "The Underlying Current",
    text: "Before you begin, pause for a moment. Take a breath.\n\nRoll your shoulders back.\n\nAsk yourself: What is asking for my attention right now? It might be a word, a feeling, a quiet pull. Don't analyze it. Just sit and notice what comes up.",
    prompt: "What is your quiet whispering to you right now?",
    ph: "Let words drift in without judgment...",
  },
  {
    id: 2,
    emoji: "2",
    label: "Learn & Grow",
    subtitle: "Send the Signal",
    text: "Select your center cell and place the word or intention that came to you in the listening step at the center of your hive. This is your queen's cell. The signal that everything else will grow from. There is no wrong center. Only an honest one.",
    prompt:
      "What is the direction the Queen is calling out? Describe your center.",
    ph: "The meaning behind my intention...",
  },
  {
    id: 3,
    emoji: "3",
    label: "Play & Create",
    subtitle: "Build the First Ring",
    text: "Now, with your signal clear, fill in the 6 cells touching your center, one thought per cell, whatever comes up first. These are the big pieces that support your center intention. Don't filter. Let the hive fill naturally.",
    prompt:
      "If your center word is a seed, what does it need around it to grow?",
    ph: "Describe what you are growing...",
  },
  {
    id: 4,
    emoji: "4",
    label: "Connect & Recharge",
    subtitle: "Let the Neighbors speak.",
    text: "Now the hive begins to speak to itself. Tap any empty cell in your hive and build outward freely. Before adding a word, look at its neighbor cells. Hold those words alongside your center. What comes from holding them together? Use that process to send signals as you build your hive. Keep going until you feel complete.",
    prompt: "What unexpected pairings were revealed?",
    ph: "What emerged when two unlikely words met?",
  },
  {
    id: 5,
    emoji: "5",
    label: "Witness & Gather",
    subtitle: "Harvest What's Sweet",
    text: "Step back and look at the full hive. Every cell is touching at least two others… nothing is isolated. What feels full and alive? Which cells want more tending? Where did you surprise yourself? This map is your signal.",
    prompt: "What does your complete hive reveal?",
    ph: "What is the queen's clearest signal today?",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// SVG TEXT — perfectly centered using tspan word wrap
// ─────────────────────────────────────────────────────────────────────────────
function HexLabel({ x, y, text, isCenter }) {
  if (!text) return null;
  const len = text.length;
  const fontSize =
    len > 13 ? 7 : len > 10 ? 8 : len > 7 ? 9.5 : len > 4 ? 11 : 12;
  const lineH = fontSize * 1.3;
  const maxChars = Math.floor((HEX_SIZE * 1.55) / (fontSize * 0.58));

  const words = text.split(" ");
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    const test = cur ? cur + " " + w : w;
    if (test.length <= maxChars) {
      cur = test;
    } else {
      if (cur) lines.push(cur);
      cur = w.length > maxChars ? w.slice(0, maxChars - 1) + "…" : w;
    }
  });
  if (cur) lines.push(cur);

  const startY = y - ((lines.length - 1) * lineH) / 2;

  return (
    <text
      textAnchor="middle"
      fontFamily="Poppins, sans-serif"
      fontWeight={isCenter ? 400 : 300}
      fill="#2e2a28"
      style={{ pointerEvents: "none", userSelect: "none" }}
    >
      {lines.map((line, i) => (
        <tspan
          key={i}
          x={x}
          y={startY + i * lineH}
          fontSize={fontSize}
          dominantBaseline="middle"
        >
          {line}
        </tspan>
      ))}
    </text>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HIVE CANVAS
// ─────────────────────────────────────────────────────────────────────────────
function HiveCanvas({ cells, frontierKeys, onCellClick, activeCell, phase }) {
  const svgRef = useRef(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(null);
  const [zoom, setZoom] = useState(1);

  const displayKeys = useMemo(() => {
    const keys = new Set(Object.keys(cells));
    if (frontierKeys) frontierKeys.forEach((k) => keys.add(k));
    return [...keys];
  }, [cells, frontierKeys]);

  useEffect(() => {
    if (!displayKeys.length) return;
    const el = svgRef.current;
    if (!el) return;
    const vw = el.clientWidth || 400,
      vh = el.clientHeight || 400;
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    displayKeys.forEach((k) => {
      const { q, r } = parseKey(k);
      const { x, y } = hexToPixel(q, r);
      minX = Math.min(minX, x - HEX_SIZE);
      maxX = Math.max(maxX, x + HEX_SIZE);
      minY = Math.min(minY, y - HEX_SIZE);
      maxY = Math.max(maxY, y + HEX_SIZE);
    });
    const pad = HEX_SIZE * 2;
    const z = Math.min(
      (vw - pad) / (maxX - minX),
      (vh - pad) / (maxY - minY),
      1.4
    );
    setZoom(z);
    setPan({
      x: vw / 2 - ((minX + maxX) / 2) * z,
      y: vh / 2 - ((minY + maxY) / 2) * z,
    });
  }, [displayKeys.length]);

  const onMouseDown = (e) => {
    if (e.target.closest("[data-hexcell]")) return;
    setDragging({ sx: e.clientX - pan.x, sy: e.clientY - pan.y });
  };
  const onMouseMove = (e) => {
    if (dragging)
      setPan({ x: e.clientX - dragging.sx, y: e.clientY - dragging.sy });
  };
  const onMouseUp = () => setDragging(null);
  const onWheel = (e) => {
    e.preventDefault();
    setZoom((z) =>
      Math.max(0.3, Math.min(2.5, z + (e.deltaY < 0 ? 0.1 : -0.1)))
    );
  };
  const touchRef = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches.length === 1)
      touchRef.current = {
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y,
      };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 1 && touchRef.current)
      setPan({
        x: e.touches[0].clientX - touchRef.current.x,
        y: e.touches[0].clientY - touchRef.current.y,
      });
  };

  const frontierSet = useMemo(
    () => new Set(frontierKeys || []),
    [frontierKeys]
  );
  const clickable = phase === "build";

  return (
    <svg
      ref={svgRef}
      style={{
        width: "100%",
        height: "100%",
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "none",
        userSelect: "none",
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={() => (touchRef.current = null)}
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {RING_FILLS.map(([f0, f1], i) => (
          <radialGradient key={i} id={`rg${i}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={f0} stopOpacity="0.92" />
            <stop offset="100%" stopColor={f1} stopOpacity="0.75" />
          </radialGradient>
        ))}
        <radialGradient id="rgActive" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#d1b8ad" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#b9a498" stopOpacity="0.7" />
        </radialGradient>
      </defs>
      <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
        {displayKeys.map((k) => {
          const { q, r } = parseKey(k);
          const { x, y } = hexToPixel(q, r);
          const content = cells[k] || "";
          const dist = Math.round(hexDist(q, r));
          const isFrontier = frontierSet.has(k) && !content;
          const isActive = activeCell === k;
          const isFilled = !!content;
          const isCenter = q === 0 && r === 0;
          const rIdx = Math.min(dist, RING_FILLS.length - 1);

          const fill = isActive
            ? "url(#rgActive)"
            : isFilled
            ? `url(#rg${rIdx})`
            : isFrontier
            ? "rgba(125,88,70,0.1)"
            : "rgba(251,248,241,0.04)";
          const stroke = isActive
            ? "#d1b8ad"
            : isFilled
            ? "rgba(125,88,70,0.55)"
            : isFrontier
            ? "rgba(125,88,70,0.28)"
            : "rgba(125,88,70,0.1)";
          const sw = (isActive ? 2 : isFilled ? 1.2 : 0.65) / zoom;

          return (
            <g
              key={k}
              data-hexcell="1"
              onClick={() => clickable && onCellClick(k)}
              style={{ cursor: clickable ? "pointer" : "default" }}
            >
              <polygon
                points={hexCorners(x, y)}
                fill={fill}
                stroke={stroke}
                strokeWidth={sw}
                filter={isActive ? "url(#glow)" : undefined}
                style={{ transition: "fill 0.18s,stroke 0.18s" }}
              />
              {isFilled ? (
                <HexLabel x={x} y={y} text={content} isCenter={isCenter} />
              ) : isFrontier ? (
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={16 / zoom}
                  fill="rgba(125,88,70,0.38)"
                  fontFamily="Poppins,sans-serif"
                  style={{ pointerEvents: "none", userSelect: "none" }}
                >
                  +
                </text>
              ) : null}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NEIGHBOR CONTEXT
// ─────────────────────────────────────────────────────────────────────────────
function NeighborContext({ activeKey, cells }) {
  if (!activeKey) return null;
  const { q, r } = parseKey(activeKey);
  if (hexDist(q, r) < 2) return null;
  const neighbors = getNeighbors(q, r)
    .map(([nq, nr]) => ({ key: hexKey(nq, nr), word: cells[hexKey(nq, nr)] }))
    .filter((n) => n.word);
  if (!neighbors.length) return null;
  const centerWord = cells["0,0"];
  return (
    <div
      style={{
        background: "rgba(185,164,152,0.1)",
        border: "1px solid rgba(185,164,152,0.26)",
        borderRadius: 10,
        padding: "11px 14px",
        marginBottom: 10,
      }}
    >
      <div
        style={{
          fontSize: 9.5,
          color: "#b9a498",
          letterSpacing: "0.13em",
          marginBottom: 7,
          fontWeight: 500,
        }}
      >
        THIS CELL TOUCHES
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: centerWord ? 8 : 0,
        }}
      >
        {neighbors.map((n) => (
          <span
            key={n.key}
            style={{
              background: "rgba(185,164,152,0.16)",
              border: "1px solid rgba(185,164,152,0.32)",
              borderRadius: 14,
              padding: "3px 11px",
              fontSize: 12,
              color: "#b9a498",
              fontStyle: "italic",
            }}
          >
            {n.word}
          </span>
        ))}
      </div>
      {centerWord && neighbors.length >= 2 && (
        <div
          style={{
            fontSize: 11,
            color: "rgba(125,88,70,0.65)",
            lineHeight: 1.6,
            fontStyle: "italic",
            fontWeight: 200,
          }}
        >
          Hold{" "}
          {neighbors
            .slice(0, 3)
            .map((n) => `"${n.word}"`)
            .join(", ")}{" "}
          against <strong style={{ color: C.honey }}>"{centerWord}"</strong> —
          what word wants to live here?
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HARVEST — AI WORD SORTER
// ─────────────────────────────────────────────────────────────────────────────
function WordSorter({ words, centerWord }) {
  const [state, setState] = useState("idle");
  const [sorted, setSorted] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const classify = async () => {
    setState("loading");
    setErrorMsg("");

    const prompt = `You are helping someone reflect on a creative clarity practice called "Build Your Hive." Their center intention was: "${centerWord}".

They filled their hive with these words: ${words.join(", ")}.

Sort these words into exactly TWO categories:

1. ACTION WORDS — things they could schedule, do, create, pursue, or give dedicated time to. These are verbs, projects, practices, habits, or concrete activities.

2. FEELING / BEING WORDS — internal states, qualities, values, or ways of being they want to cultivate or protect time for. These are emotions, energies, mindsets, or ways of showing up.

For each category, write one short warm sentence (1–2 lines) that helps them think about how to put that list to practical use — either scheduling actions or protecting space for the feelings.

Respond ONLY with valid JSON, no markdown fences, no preamble, no trailing text:
{"actions":{"words":["..."],"prompt":"..."},"feelings":{"words":["..."],"prompt":"..."},"uncategorized":["..."]}`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key":
            "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",
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 800,
          messages: [{ role: "user", content: prompt }],
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`API error ${res.status}: ${errText}`);
      }

      const data = await res.json();

      // Extract text from all content blocks
      const raw = data.content
        .filter((b) => b.type === "text")
        .map((b) => b.text || "")
        .join("")
        .replace(/```json\s*/g, "")
        .replace(/```\s*/g, "")
        .trim();

      // Find the JSON object in the response
      const jsonMatch = raw.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("No JSON found in response");

      const parsed = JSON.parse(jsonMatch[0]);
      setSorted(parsed);
      setState("done");
    } catch (e) {
      console.error("WordSorter error:", e);
      setErrorMsg(e.message || "Unknown error");
      setState("error");
    }
  };

  const Card = ({ icon, title, color, border, bg, items, blurb }) => (
    <div
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 12,
        padding: "16px 18px",
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 5,
        }}
      >
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div
          style={{
            fontSize: 10.5,
            color,
            fontWeight: 500,
            letterSpacing: "0.1em",
          }}
        >
          {title}
        </div>
      </div>
      <p
        style={{
          fontSize: 12.5,
          color: C.muted,
          fontStyle: "italic",
          margin: "0 0 11px",
          lineHeight: 1.75,
          fontWeight: 200,
        }}
      >
        {blurb}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
        {items && items.length ? (
          items.map((w, i) => (
            <span
              key={i}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${border}`,
                borderRadius: 20,
                padding: "5px 13px",
                fontSize: 12.5,
                color: C.text,
                fontWeight: 300,
              }}
            >
              {w}
            </span>
          ))
        ) : (
          <span style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>
            None identified
          </span>
        )}
      </div>
    </div>
  );

  if (state === "idle")
    return (
      <div style={{ textAlign: "center", padding: "20px 0 6px" }}>
        <p
          style={{
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.85,
            margin: "0 0 16px",
            fontWeight: 200,
          }}
        >
          Your hive words hold two kinds of energy — things to <em>do</em> and
          ways to <em>be</em>.<br />
          Let's sort them so you can put them to work.
        </p>
        <button
          onClick={classify}
          style={{
            background: `linear-gradient(135deg,${C.gold},${C.honey})`,
            border: "none",
            borderRadius: 40,
            padding: "11px 28px",
            fontSize: 13,
            fontWeight: 300,
            color: C.dark,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            boxShadow: "0 3px 16px rgba(125,88,70,0.26)",
          }}
        >
          Sort My Hive Words ⬡
        </button>
      </div>
    );

  if (state === "loading")
    return (
      <div style={{ textAlign: "center", padding: "26px 0" }}>
        <div
          style={{
            fontSize: 26,
            display: "inline-block",
            animation: "spin 2s linear infinite",
          }}
        >
          ⬡
        </div>
        <p
          style={{
            fontSize: 12,
            color: C.muted,
            fontWeight: 200,
            margin: "8px 0 0",
          }}
        >
          Reading your hive…
        </p>
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );

  if (state === "error")
    return (
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <p
          style={{
            fontSize: 12,
            color: C.muted,
            fontWeight: 200,
            margin: "0 0 12px",
          }}
        >
          Couldn't sort right now — try again in a moment.
        </p>
        {errorMsg && (
          <p
            style={{
              fontSize: 10,
              color: "rgba(185,164,152,0.4)",
              fontFamily: "monospace",
              margin: "0 0 12px",
            }}
          >
            {errorMsg}
          </p>
        )}
        <button
          onClick={classify}
          style={{
            background: "rgba(125,88,70,0.2)",
            border: "1px solid rgba(125,88,70,0.3)",
            borderRadius: 40,
            padding: "8px 20px",
            fontSize: 12,
            color: C.honey,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            fontWeight: 300,
          }}
        >
          Try Again
        </button>
      </div>
    );

  if (state === "done" && sorted)
    return (
      <div>
        <Card
          icon="🗓"
          title="ACTION WORDS — SCHEDULE THESE"
          color="#b9a498"
          border="rgba(125,88,70,0.32)"
          bg="rgba(125,88,70,0.07)"
          items={sorted.actions?.words || []}
          blurb={sorted.actions?.prompt || ""}
        />
        <Card
          icon="🌿"
          title="FEELING WORDS — PROTECT THESE"
          color="#a3a7ab"
          border="rgba(163,167,171,0.32)"
          bg="rgba(163,167,171,0.07)"
          items={sorted.feelings?.words || []}
          blurb={sorted.feelings?.prompt || ""}
        />
        {sorted.uncategorized?.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div
              style={{
                fontSize: 10,
                color: C.dimText,
                letterSpacing: "0.1em",
                marginBottom: 7,
              }}
            >
              ALSO IN YOUR HIVE
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sorted.uncategorized.map((w, i) => (
                <span
                  key={i}
                  style={{
                    background: "rgba(75,69,69,0.2)",
                    border: "1px solid rgba(75,69,69,0.28)",
                    borderRadius: 20,
                    padding: "4px 11px",
                    fontSize: 12,
                    color: C.muted,
                    fontWeight: 200,
                  }}
                >
                  {w}
                </span>
              ))}
            </div>
          </div>
        )}
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            onClick={() => {
              setSorted(null);
              setState("idle");
            }}
            style={{
              background: "transparent",
              border: "1px solid rgba(125,88,70,0.2)",
              borderRadius: 40,
              padding: "6px 16px",
              fontSize: 11,
              color: "rgba(185,164,152,0.5)",
              cursor: "pointer",
              fontFamily: "'Poppins',sans-serif",
              fontWeight: 300,
            }}
          >
            Re-sort
          </button>
        </div>
      </div>
    );

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// HARVEST VIEW
// ─────────────────────────────────────────────────────────────────────────────
function HarvestView({ cells, reflections, elapsed, onReset }) {
  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(
      2,
      "0"
    )}`;
  const centerWord = cells["0,0"] || "—";
  const totalCells = Object.keys(cells).length;
  const distVals = Object.keys(cells).map((k) => {
    const { q, r } = parseKey(k);
    return hexDist(q, r);
  });
  const maxRing = distVals.length ? Math.round(Math.max(...distVals)) : 0;
  const allWords = Object.entries(cells)
    .filter(([k]) => k !== "0,0")
    .map(([, v]) => v)
    .filter(Boolean);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(160deg,${C.dark} 0%,${C.dark2} 60%,#1e1a18 100%)`,
        fontFamily: "'Poppins',sans-serif",
        fontWeight: 200,
        color: C.cream,
        padding: "32px 20px 60px",
      }}
    >
      <div style={{ maxWidth: 500, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ fontSize: 34, marginBottom: 6 }}>🍯</div>
          <h2
            style={{
              fontSize: 21,
              color: C.honey,
              margin: "0 0 4px",
              fontWeight: 300,
            }}
          >
            Your Honey is on the Way!
          </h2>
          <p
            style={{
              color: C.muted,
              fontSize: 11.5,
              margin: 0,
              fontWeight: 200,
            }}
          >
            {totalCells} cells · {maxRing + 1} ring{maxRing > 0 ? "s" : ""} ·{" "}
            {fmt(elapsed)}
          </p>
        </div>

        {/* Hive — pannable */}
        <div
          style={{
            background: "rgba(75,69,69,0.15)",
            border: "1px solid rgba(125,88,70,0.18)",
            borderRadius: 14,
            padding: 12,
            marginBottom: 20,
            height: 280,
          }}
        >
          <HiveCanvas
            cells={cells}
            frontierKeys={[]}
            onCellClick={() => {}}
            activeCell={null}
            phase="view"
          />
        </div>

        {/* Anchor */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 20,
            paddingBottom: 20,
            borderBottom: "1px solid rgba(125,88,70,0.14)",
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: C.dimText,
              letterSpacing: "0.13em",
              marginBottom: 5,
            }}
          >
            QUEEN'S CELL · YOUR CENTER
          </div>
          <div
            style={{
              fontSize: 21,
              color: C.honey,
              fontWeight: 300,
              fontStyle: "italic",
            }}
          >
            "{centerWord}"
          </div>
        </div>

        {/* Word sorter */}
        <div
          style={{
            marginBottom: 24,
            paddingBottom: 24,
            borderBottom: "1px solid rgba(125,88,70,0.11)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: C.honey,
              fontWeight: 500,
              letterSpacing: "0.1em",
              marginBottom: 2,
            }}
          >
            ⬡ YOUR HIVE WORDS
          </div>
          <WordSorter words={allWords} centerWord={centerWord} />
        </div>

        {/* Reflections */}
        {Object.values(reflections).some(Boolean) && (
          <div
            style={{
              background: "rgba(75,69,69,0.12)",
              border: "1px solid rgba(75,69,69,0.2)",
              borderRadius: 10,
              padding: "16px",
              marginBottom: 22,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: C.honey,
                fontWeight: 500,
                marginBottom: 12,
                letterSpacing: "0.1em",
              }}
            >
              YOUR REFLECTIONS
            </div>
            {STEPS.map((st, i) =>
              reflections[i] ? (
                <div key={i} style={{ marginBottom: 13 }}>
                  <div
                    style={{ fontSize: 10, color: C.dimText, marginBottom: 3 }}
                  >
                    {st.emoji} {st.label.toUpperCase()}
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: C.text,
                      margin: 0,
                      lineHeight: 1.78,
                      fontWeight: 200,
                    }}
                  >
                    {reflections[i]}
                  </p>
                </div>
              ) : null
            )}
          </div>
        )}

        {/* Quote */}
        <div
          style={{
            borderLeft: `3px solid ${C.gold}`,
            paddingLeft: 16,
            marginBottom: 28,
          }}
        >
          <p
            style={{
              fontSize: 13.5,
              fontStyle: "italic",
              color: C.honey,
              margin: 0,
              lineHeight: 1.85,
              fontWeight: 200,
            }}
          >
            "Every hive produces a honey drop - the insight that holds the
            sweetness of everything you gathered."
          </p>
        </div>

        <button
          onClick={onReset}
          style={{
            width: "100%",
            background: "rgba(75,69,69,0.18)",
            border: "1px solid rgba(125,88,70,0.26)",
            borderRadius: 40,
            padding: 12,
            fontSize: 13,
            color: C.honey,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            fontWeight: 300,
          }}
        >
          ⬡ Begin a New Hive
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("intro");
  const [teachStep, setTeachStep] = useState(0);
  const [actStep, setActStep] = useState(0);

  const [cells, setCells] = useState({});
  const [activeCell, setActiveCell] = useState(null);
  const [cellInput, setCellInput] = useState("");
  const [maxRingShown, setMaxRingShown] = useState(0);

  const [reflections, setReflections] = useState({});
  const [curReflection, setCurReflection] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  };
  useEffect(() => () => clearInterval(timerRef.current), []);
  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(
      2,
      "0"
    )}`;

  // Frontier cells
  const frontierKeys = useMemo(() => {
    if (actStep < 1) return [];
    if (actStep === 1) return cells["0,0"] ? [] : ["0,0"];
    // Step 3: only ring 1 cells
    if (actStep === 2) {
      return getRing(1)
        .map(([q, r]) => hexKey(q, r))
        .filter((k) => !cells[k]);
    }
    // Step 4+: all empty cells adjacent to any filled cell
    return getFrontierCells(cells).filter((k) => {
      const { q, r } = parseKey(k);
      return hexDist(q, r) <= maxRingShown + 1;
    });
  }, [cells, maxRingShown, actStep]);

  const handleCellClick = (key) => {
    if (!cells[key] && !frontierKeys.includes(key)) return;
    setActiveCell(key);
    setCellInput(cells[key] || "");
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const handleSave = () => {
    const val = cellInput.trim();
    if (!val || !activeCell) return;
    const newCells = { ...cells, [activeCell]: val };
    setCells(newCells);
    const { q, r } = parseKey(activeCell);
    const d = Math.round(hexDist(q, r));
    const newMax = Math.max(maxRingShown, d);
    if (d > maxRingShown) setMaxRingShown(d);

    // Step 3: stay within ring 1, auto-advance to step 4 when ring 1 complete
    if (actStep === 2) {
      const ring1Keys = getRing(1).map(([q, r]) => hexKey(q, r));
      const nextRing1 = ring1Keys.find((k) => k !== activeCell && !newCells[k]);
      if (nextRing1) {
        setActiveCell(nextRing1);
        setCellInput("");
        setTimeout(() => inputRef.current?.focus(), 60);
      } else {
        // All 6 filled — move to step 4
        setActiveCell(null);
        setCellInput("");
        setReflections((p) => ({ ...p, [actStep]: curReflection }));
        setActStep(3);
        setCurReflection(reflections[3] || "");
        setMaxRingShown((r) => Math.max(r, 2));
      }
      return;
    }

    // All other steps: advance to next frontier cell
    const nextFrontier = getFrontierCells(newCells).filter((k) => {
      const { q: nq, r: nr } = parseKey(k);
      return hexDist(nq, nr) <= newMax + 1;
    });
    const next = nextFrontier.find((k) => k !== activeCell);
    if (next) {
      setActiveCell(next);
      setCellInput("");
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      setActiveCell(null);
      setCellInput("");
    }
  };

  const addNextRing = () => {
    const filledDists = Object.keys(cells).map((k) => {
      const { q, r } = parseKey(k);
      return Math.round(hexDist(q, r));
    });
    const outermost = filledDists.length ? Math.max(...filledDists) : 0;
    const newMax = Math.max(outermost + 1, maxRingShown + 1);
    setMaxRingShown(newMax);
    setActiveCell(null);
    setCellInput("");
  };

  const finishBuilding = () => {
    setReflections((p) => ({ ...p, [actStep]: curReflection }));
    setScreen("harvest");
    clearInterval(timerRef.current);
  };

  const goActStep = (dir) => {
    setReflections((p) => ({ ...p, [actStep]: curReflection }));
    const next = actStep + dir;
    setActStep(next);
    setCurReflection(reflections[next] || "");
    setCellInput("");
    if (next === 2 && cells["0,0"]) setMaxRingShown(1);
    if (next === 3) setMaxRingShown((r) => Math.max(r, 2));
    // Auto-select first available frontier cell when entering ring steps
    if (next === 2) {
      // Enter ring1 step — auto-highlight first ring1 cell
      const ring1 = getRing(1);
      const firstEmpty = ring1.find(([q, r]) => !cells[hexKey(q, r)]);
      if (firstEmpty) {
        const fk = hexKey(firstEmpty[0], firstEmpty[1]);
        setActiveCell(fk);
        setTimeout(() => inputRef.current?.focus(), 80);
      } else {
        setActiveCell(null);
      }
    } else {
      setActiveCell(null);
    }
  };

  const reset = () => {
    setCells({});
    setActiveCell(null);
    setCellInput("");
    setMaxRingShown(0);
    setReflections({});
    setCurReflection("");
    setElapsed(0);
    setActStep(0);
    setTeachStep(0);
    clearInterval(timerRef.current);
    setScreen("intro");
  };

  const s = STEPS[actStep];
  const showHive = actStep >= 1;
  const isCenterStep = actStep === 1;
  const isRing1Step = actStep === 2;
  const isExtendStep = actStep === 3;
  const isWitnessStep = actStep === 4;
  const filledCount = Object.keys(cells).length;

  // ── INTRO ─────────────────────────────────────────────────────────────────
  if (screen === "intro")
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `linear-gradient(160deg,${C.dark} 0%,${C.dark2} 55%,#1e1a18 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          fontFamily: "'Poppins',sans-serif",
          fontWeight: 200,
          color: C.cream,
        }}
      >
        <svg
          viewBox="-110 -110 220 220"
          style={{ width: 128, marginBottom: 16, opacity: 0.84 }}
        >
          {[
            [0, 0, 0],
            ...getRing(1).map(([q, r]) => [q, r, 1]),
            ...getRing(2).map(([q, r]) => [q, r, 2]),
          ].map(([q, r, ring], i) => {
            const { x, y } = hexToPixel(q, r, 20);
            return (
              <polygon
                key={i}
                points={hexCorners(x, y, 19)}
                fill={
                  ring === 0
                    ? "rgba(125,88,70,0.42)"
                    : ring === 1
                    ? "rgba(125,88,70,0.24)"
                    : "rgba(125,88,70,0.12)"
                }
                stroke="rgba(125,88,70,0.6)"
                strokeWidth="0.8"
              />
            );
          })}
        </svg>
        <h1
          style={{
            fontSize: "clamp(20px,5vw,34px)",
            fontWeight: 300,
            color: C.honey,
            letterSpacing: "0.1em",
            textAlign: "center",
            margin: "0 0 4px",
          }}
        >
          THE CREATIVE MIND HIVE
        </h1>
        <p
          style={{
            fontSize: 13.5,
            color: "#c1bbbb",
            fontStyle: "italic",
            textAlign: "center",
            margin: "0 0 3px",
            fontWeight: 200,
          }}
        >
          A Clarity Practice To Tap Into The Honey of Life
        </p>
        <p
          style={{
            fontSize: 10,
            color: "#919181",
            textAlign: "center",
            margin: "0 0 28px",
            letterSpacing: "0.15em",
            fontWeight: 300,
          }}
        >
          Created by Willow Grace Healing Arts
        </p>
        <div
          style={{
            maxWidth: 420,
            background: "rgba(75,69,69,0.18)",
            border: "1px solid rgba(125,88,70,0.2)",
            borderRadius: 14,
            padding: "22px 26px",
            marginBottom: 26,
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: 13.5,
              lineHeight: 1.95,
              color: C.text,
              margin: "0 0 13px",
              fontWeight: 200,
            }}
          >
            Sometimes we know something matters - we just can't see it clearly
            yet. The Mind Hive helps you explore what's already forming in your
            thoughts and clarify your signal.
          </p>
          <div
            style={{
              height: 1,
              background:
                "linear-gradient(90deg,transparent,rgba(125,88,70,0.22),transparent)",
              margin: "0 0 13px",
            }}
          />
          <p
            style={{
              fontSize: 13.5,
              lineHeight: 1.95,
              color: C.text,
              margin: "0 0 13px",
              fontWeight: 200,
            }}
          >
            You'll begin with one word at the center - an intention, a question,
            or simply where you are right now. From there, you'll build outward,
            cell by cell, letting each new word be shaped by what it touches.
          </p>
          <p
            style={{
              fontSize: 13.5,
              lineHeight: 1.95,
              color: C.text,
              margin: 0,
              fontWeight: 200,
            }}
          >
            As your hive grows, something interesting begins to happen: patterns
            appear, connections strengthen, and the mind begins organizing
            itself. What emerges isn't just a plan or a to-do list. It's a way
            to see how your thoughts and desires relate to each other. A
            honeycomb of your own thinking, showcasing what's alive and true
            within. Use this to tap into the sweet honey pot of life.
          </p>
        </div>
        <button
          onClick={() => {
            setScreen("teaching");
            startTimer();
          }}
          style={{
            background: `linear-gradient(135deg,${C.gold},${C.honey})`,
            border: "none",
            borderRadius: 40,
            padding: "13px 42px",
            fontSize: 14,
            fontWeight: 300,
            color: C.dark,
            cursor: "pointer",
            letterSpacing: "0.06em",
            fontFamily: "'Poppins',sans-serif",
            boxShadow: "0 4px 22px rgba(125,88,70,0.28)",
          }}
        >
          Enter the Hive ⬡
        </button>
      </div>
    );

  // ── TEACHING ──────────────────────────────────────────────────────────────
  if (screen === "teaching") {
    const teachings = [
      {
        icon: "The Center Cell",
        title: "The Queen Bee Signal",
        body: "Every hive begins with a center cell. What is placed in this cell becomes your signal - an intention - and what you will keep coming back to as you build your hive. It might be a question, a project, a feeling, a value, a season of life. Whetever it is for you, everything in your hive will grow outward from this center.\n\nChoose what reflects and feels most present and right for you in this moment.",
        quote:
          "When you are clear, you don't have to work as hard. You can simply be.",
      },
      {
        icon: "The Hive Structure",
        title: "The Sacred Hexagon",
        body: "Your hive is made of connected hexagon cells. Each cell holds one word or short phrase. New cells connect together perfectly, allowing ideas to grow naturally from and strengthen each other. As you build your hive, begin to notice patterns of connection you hadn't seen before.\n\nTo help formulate what to place in a new cell, hold the signal of the words that surround it and your original intention together. Pause and listen and see what arises. There is no right or wrong way to build your hive. Just follow what feels natural as you go.",
        quote:
          "The hexagon does not compete for space. It simply fits perfectly, endlessly, with every neighbor.",
      },
    ];
    const t = teachings[teachStep];
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `linear-gradient(160deg,${C.dark} 0%,${C.dark2} 60%,#1e1a18 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          fontFamily: "'Poppins',sans-serif",
          fontWeight: 200,
          color: C.cream,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 18,
            right: 18,
            color: "rgba(125,88,70,0.48)",
            fontSize: 11,
          }}
        >
          {fmt(elapsed)} ⏱
        </div>
        <div style={{ maxWidth: 480, width: "100%" }}>
          <div style={{ fontSize: 34, textAlign: "center", marginBottom: 8 }}>
            {t.icon}
          </div>
          <h2
            style={{
              fontSize: 20,
              color: C.honey,
              textAlign: "center",
              margin: "0 0 20px",
              fontWeight: 300,
            }}
          >
            {t.title}
          </h2>
          <div
            style={{
              background: "rgba(75,69,69,0.18)",
              border: "1px solid rgba(125,88,70,0.18)",
              borderRadius: 12,
              padding: "20px 24px",
              marginBottom: 16,
            }}
          >
            <p
              style={{
                fontSize: 13.5,
                lineHeight: 1.92,
                color: C.text,
                margin: 0,
                whiteSpace: "pre-line",
                fontWeight: 200,
              }}
            >
              {t.body}
            </p>
          </div>
          <div
            style={{
              borderLeft: `3px solid ${C.gold}`,
              paddingLeft: 15,
              marginBottom: 28,
            }}
          >
            <p
              style={{
                fontSize: 14,
                fontStyle: "italic",
                color: C.honey,
                margin: 0,
                lineHeight: 1.7,
                fontWeight: 200,
              }}
            >
              "{t.quote}"
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            {teachStep > 0 && (
              <button
                onClick={() => setTeachStep(0)}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(125,88,70,0.28)",
                  borderRadius: 40,
                  padding: "9px 22px",
                  fontSize: 12,
                  color: C.gold,
                  cursor: "pointer",
                  fontFamily: "'Poppins',sans-serif",
                  fontWeight: 300,
                }}
              >
                ← Back
              </button>
            )}
            {teachStep === 0 ? (
              <button
                onClick={() => setTeachStep(1)}
                style={{
                  background: `linear-gradient(135deg,${C.gold},${C.honey})`,
                  border: "none",
                  borderRadius: 40,
                  padding: "10px 26px",
                  fontSize: 13,
                  fontWeight: 300,
                  color: C.dark,
                  cursor: "pointer",
                  fontFamily: "'Poppins',sans-serif",
                }}
              >
                Continue →
              </button>
            ) : (
              <button
                onClick={() => {
                  setScreen("activity");
                  setActStep(0);
                  setCurReflection("");
                }}
                style={{
                  background: `linear-gradient(135deg,${C.gold},${C.honey})`,
                  border: "none",
                  borderRadius: 40,
                  padding: "11px 30px",
                  fontSize: 13,
                  fontWeight: 300,
                  color: C.dark,
                  cursor: "pointer",
                  fontFamily: "'Poppins',sans-serif",
                  boxShadow: "0 4px 18px rgba(125,88,70,0.26)",
                }}
              >
                Begin the Activity ⬡
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── ACTIVITY ──────────────────────────────────────────────────────────────
  if (screen === "activity")
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: `linear-gradient(160deg,${C.dark} 0%,${C.dark2} 60%,#1e1a18 100%)`,
          fontFamily: "'Poppins',sans-serif",
          fontWeight: 200,
          color: C.cream,
        }}
      >
        {/* Top bar */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "11px 18px",
            borderBottom: "1px solid rgba(125,88,70,0.14)",
          }}
        >
          <div style={{ display: "flex", gap: 5 }}>
            {STEPS.map((_, i) => (
              <div
                key={i}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: i <= actStep ? C.gold : "rgba(125,88,70,0.18)",
                  transition: "all 0.25s",
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            {filledCount > 0 && (
              <span style={{ fontSize: 10, color: C.dimText }}>
                {filledCount} cells
              </span>
            )}
            <span style={{ color: "rgba(125,88,70,0.5)", fontSize: 11 }}>
              {fmt(elapsed)} ⏱
            </span>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 17px 24px" }}>
          <div style={{ maxWidth: 500, margin: "0 auto" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                marginBottom: 2,
              }}
            >
              <span style={{ fontSize: 20 }}>{s.emoji}</span>
              <div>
                <div
                  style={{
                    fontSize: 9,
                    color: "rgba(125,88,70,0.5)",
                    letterSpacing: "0.14em",
                  }}
                >
                  STEP {s.id} OF 5
                </div>
                <h2
                  style={{
                    fontSize: 17,
                    color: C.honey,
                    margin: 0,
                    fontWeight: 300,
                  }}
                >
                  {s.label}
                </h2>
              </div>
            </div>
            <p
              style={{
                fontSize: 11,
                color: C.muted,
                fontStyle: "italic",
                margin: "0 0 12px",
                fontWeight: 200,
              }}
            >
              {s.subtitle}
            </p>

            <div
              style={{
                background: "rgba(75,69,69,0.17)",
                border: "1px solid rgba(125,88,70,0.17)",
                borderRadius: 10,
                padding: "13px 16px",
                marginBottom: 12,
                fontSize: 13,
                lineHeight: 1.9,
                color: C.text,
                fontWeight: 200,
              }}
            >
              {s.text}
            </div>

            {showHive && (
              <div style={{ marginBottom: 10 }}>
                <div
                  style={{
                    fontSize: 9,
                    color: "rgba(125,88,70,0.42)",
                    letterSpacing: "0.12em",
                    textAlign: "center",
                    marginBottom: 4,
                  }}
                >
                  {isCenterStep && "TAP THE CENTER CELL"}
                  {isRing1Step &&
                    "FILL ALL 6 CELLS · THEN YOU'LL MOVE ON AUTOMATICALLY"}
                  {isExtendStep && "TAP ANY CELL TO FILL IT · DRAG TO PAN"}
                  {isWitnessStep && "YOUR COMPLETE HIVE · DRAG TO PAN"}
                </div>
                <div
                  style={{
                    height: isExtendStep || isWitnessStep ? 310 : 250,
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "rgba(75,69,69,0.09)",
                    border: "1px solid rgba(75,69,69,0.16)",
                  }}
                >
                  <HiveCanvas
                    cells={cells}
                    frontierKeys={isWitnessStep ? [] : frontierKeys}
                    onCellClick={handleCellClick}
                    activeCell={activeCell}
                    phase={isWitnessStep ? "view" : "build"}
                  />
                </div>

                {(isExtendStep || isRing1Step) && activeCell && (
                  <div style={{ marginTop: 8 }}>
                    <NeighborContext activeKey={activeCell} cells={cells} />
                  </div>
                )}

                {!isWitnessStep && activeCell && (
                  <div style={{ marginTop: 10 }}>
                    <div
                      style={{
                        fontSize: 9,
                        color: "rgba(125,88,70,0.55)",
                        marginBottom: 4,
                        letterSpacing: "0.1em",
                      }}
                    >
                      {activeCell === "0,0"
                        ? "QUEEN'S CELL"
                        : isExtendStep
                        ? "WHAT EMERGES HERE?"
                        : "FILL THIS CELL"}
                    </div>
                    <div style={{ display: "flex", gap: 7 }}>
                      <input
                        ref={inputRef}
                        value={cellInput}
                        onChange={(e) => setCellInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave()}
                        placeholder="First word that arrives..."
                        style={{
                          flex: 1,
                          background: "rgba(75,69,69,0.2)",
                          border: "1px solid rgba(125,88,70,0.26)",
                          borderRadius: 7,
                          padding: "9px 12px",
                          color: C.cream,
                          fontSize: 14,
                          fontFamily: "'Poppins',sans-serif",
                          fontWeight: 200,
                          outline: "none",
                        }}
                      />
                      <button
                        onClick={handleSave}
                        style={{
                          background: `linear-gradient(135deg,${C.gold},${C.honey})`,
                          border: "none",
                          borderRadius: 7,
                          padding: "9px 14px",
                          fontSize: 13,
                          color: C.dark,
                          cursor: "pointer",
                          fontWeight: 400,
                        }}
                      >
                        ⬡
                      </button>
                    </div>
                  </div>
                )}

                {isExtendStep && filledCount > 1 && (
                  <div style={{ marginTop: 11 }}>
                    <button
                      onClick={() => goActStep(1)}
                      style={{
                        width: "100%",
                        background: "rgba(75,69,69,0.14)",
                        border: "1px solid rgba(125,88,70,0.22)",
                        borderRadius: 8,
                        padding: "10px",
                        fontSize: 12,
                        color: C.muted,
                        cursor: "pointer",
                        fontFamily: "'Poppins',sans-serif",
                        fontWeight: 300,
                      }}
                    >
                      I'm Done Building →
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: 18 }}>
              <div
                style={{
                  fontSize: 12,
                  color: C.honey,
                  fontStyle: "italic",
                  marginBottom: 5,
                  fontWeight: 300,
                }}
              >
                {s.prompt}
              </div>
              <textarea
                value={curReflection}
                onChange={(e) => setCurReflection(e.target.value)}
                placeholder={s.ph}
                rows={3}
                style={{
                  width: "100%",
                  background: "rgba(75,69,69,0.13)",
                  border: "1px solid rgba(125,88,70,0.14)",
                  borderRadius: 8,
                  padding: "9px 11px",
                  color: C.text,
                  fontSize: 13,
                  fontFamily: "'Poppins',sans-serif",
                  fontWeight: 200,
                  resize: "none",
                  outline: "none",
                  boxSizing: "border-box",
                  lineHeight: 1.75,
                }}
              />
            </div>
          </div>
        </div>

        {/* Bottom nav */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            gap: 8,
            justifyContent: "space-between",
            padding: "12px 17px",
            borderTop: "1px solid rgba(75,69,69,0.18)",
            background: "rgba(46,42,40,0.95)",
          }}
        >
          {actStep > 0 ? (
            <button
              onClick={() => goActStep(-1)}
              style={{
                background: "transparent",
                border: "1px solid rgba(125,88,70,0.24)",
                borderRadius: 40,
                padding: "9px 18px",
                fontSize: 12,
                color: C.gold,
                cursor: "pointer",
                fontFamily: "'Poppins',sans-serif",
                fontWeight: 300,
              }}
            >
              ← Back
            </button>
          ) : (
            <div />
          )}
          {actStep < STEPS.length - 1 && !isExtendStep ? (
            <button
              onClick={() => {
                if (actStep === 1 && cells["0,0"]) setMaxRingShown(1);
                if (actStep === 2) setMaxRingShown((r) => Math.max(r, 2));
                goActStep(1);
              }}
              style={{
                background: `linear-gradient(135deg,${C.gold},${C.honey})`,
                border: "none",
                borderRadius: 40,
                padding: "10px 26px",
                fontSize: 13,
                fontWeight: 300,
                color: C.dark,
                cursor: "pointer",
                fontFamily: "'Poppins',sans-serif",
              }}
            >
              Next Step →
            </button>
          ) : isExtendStep ? null : (
            <button
              onClick={finishBuilding}
              style={{
                background: `linear-gradient(135deg,${C.gold},${C.honey})`,
                border: "none",
                borderRadius: 40,
                padding: "10px 22px",
                fontSize: 13,
                fontWeight: 300,
                color: C.dark,
                cursor: "pointer",
                fontFamily: "'Poppins',sans-serif",
                boxShadow: "0 4px 18px rgba(125,88,70,0.28)",
              }}
            >
              Generate My Hive Overview 🍯
            </button>
          )}
        </div>
      </div>
    );

  // ── HARVEST ───────────────────────────────────────────────────────────────
  if (screen === "harvest")
    return (
      <HarvestView
        cells={cells}
        reflections={reflections}
        elapsed={elapsed}
        onReset={reset}
      />
    );
}
